import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { ArticleBody } from "@/components/article/ArticleBody";
import { ArticleLinkPanel } from "@/components/article/ArticleLinkPanel";
import { BN_POSTS, isDraftBnPost, loadBnPost, type BnPost } from "@/content/bn/posts";
import { BN_CATEGORY_KEYWORDS } from "@/content/bn/keywords";
import { BN_CATEGORY_SLUGS, bnCategoryToSlug } from "@/content/bn/categories";
import { resolveBnInternalLinks, BN_GROUP_ORDER } from "@/content/bn/internal-links";
import { SITE } from "@/content/site";
import { bnPageHead } from "@/lib/bn-head";
import { breadcrumbSchema } from "@/lib/seo";
import { articleAuthor, articlePublisher } from "@/lib/article-schema";

export const Route = createFileRoute("/bn/blog/$slug")({
  loader: async ({ params }) => {
    const p = await loadBnPost(params.slug);
    if (!p) throw notFound();
    return { p };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.p;
    if (!p) return { meta: [{ title: "পোস্ট পাওয়া যায়নি" }] };
    return bnPageHead({
      path: `/bn/blog/${params.slug}`,
      enPath: p.enPath,
      title: p.title,
      desc: p.excerpt,
      keywords: [p.title, BN_CATEGORY_KEYWORDS[p.category]],
      type: "article",
      og: [
        { property: "article:published_time", content: p.date },
        { property: "article:modified_time", content: p.updated ?? p.date },
        { property: "article:author", content: SITE.fullName },
      ],
      twitterCard: false,
      // Unreleased drafts stay reachable by URL for review but never enter the index.
      tail: isDraftBnPost(p.slug) ? [{ name: "robots", content: "noindex, follow" }] : [],
    });
  },
  notFoundComponent: () => (
    <Section className="py-24">
      <h1 className="font-display text-3xl">পোস্ট পাওয়া যায়নি</h1>
      <Link to="/bn/blog" className="mt-4 inline-flex items-center gap-1 text-primary">
        <ArrowLeft className="h-4 w-4" /> সব আর্টিকেলে ফিরে যান
      </Link>
    </Section>
  ),
  component: BnPost,
});

function BnPost() {
  const { p } = Route.useLoaderData() as { p: BnPost };
  const url = `${SITE.url}/bn/blog/${p.slug}`;
  const related = BN_POSTS.filter((x) => x.slug !== p.slug).slice(0, 3);
  const categorySlug = bnCategoryToSlug(p.category);
  const relatedCategories = Object.entries(BN_CATEGORY_SLUGS)
    .map(([slug, name]) => ({
      slug,
      name,
      count: BN_POSTS.filter((x) => x.category === name).length,
    }))
    .filter((c) => c.count > 0 && c.slug !== categorySlug)
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);

  // Article JSON-LD mirrors the English template so both languages expose the
  // same entity, authorship and revision signals to crawlers.
  const enUrl = p.enPath
    ? p.enPath.startsWith("http")
      ? p.enPath
      : `${SITE.url}${p.enPath}`
    : null;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline: p.title,
    description: p.excerpt,
    datePublished: p.date,
    dateModified: p.updated ?? p.date,
    inLanguage: "bn-BD",
    articleSection: p.category,
    author: articleAuthor(),
    publisher: articlePublisher(),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    // Declares the English original so both versions resolve to one work.
    ...(enUrl ? { translationOfWork: { "@id": `${enUrl}#article` } } : {}),
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".aeo-speakable"] },
    wordCount: p.body
      .map((b) => `${b.h ?? ""} ${b.h3 ?? ""} ${b.p ?? ""} ${b.a ?? ""}`)
      .join(" ")
      .trim()
      .split(/\s+/).length,
  };

  // Only emit FAQPage when there are real Q&A pairs — an empty mainEntity is
  // invalid markup.
  const faqSchema =
    p.faq && p.faq.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${url}#faq`,
          inLanguage: "bn-BD",
          mainEntity: p.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  const breadcrumbs = breadcrumbSchema([
    { name: "হোম", path: "/bn" },
    { name: "ব্লগ", path: "/bn/blog" },
    { name: p.title, path: `/bn/blog/${p.slug}` },
  ]);

  return (
    <>
      <JsonLd data={articleSchema} />
      {faqSchema && <JsonLd data={faqSchema} />}
      <JsonLd data={breadcrumbs} />

      <Section className="pt-8 pb-12">
        <nav className="mb-8 text-sm text-muted-foreground">
          <Link to="/bn" className="hover:text-foreground">হোম</Link>
          <span className="mx-2">/</span>
          <Link to="/bn/blog" className="hover:text-foreground">ব্লগ</Link>
        </nav>

        <header className="max-w-3xl">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {categorySlug ? (
              <Link
                to="/bn/blog/category/$category"
                params={{ category: categorySlug }}
                className="text-primary hover:underline underline-offset-4"
              >
                {p.category}
              </Link>
            ) : (
              <span>{p.category}</span>
            )}
            {" · "}{p.readMin} মিনিট পড়া · {new Date(p.date).toLocaleDateString("bn-BD")}
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {p.title}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">{p.excerpt}</p>
        </header>
      </Section>

      <Section className="pb-12">
        <div className="grid gap-12 lg:grid-cols-[1fr_280px]">
          <article className="max-w-3xl">
            {p.takeaways.length > 0 && (
              <aside className="mb-10 rounded-2xl border border-border bg-card p-6">
                <h2 className="font-display text-lg mb-3">মূল বিষয়সমূহ</h2>
                <ul className="space-y-2 text-sm">
                  {p.takeaways.map((t, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-primary">→</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </aside>
            )}

            <ArticleBody blocks={p.body} lang="bn" />

            <section className="mt-16 border-t border-border pt-10">
              <h2 className="font-display text-2xl mb-6">প্রায়শই জিজ্ঞাসিত প্রশ্ন</h2>
              <div className="space-y-6">
                {p.faq.map((f, i) => (
                  <div key={i}>
                    <h3 className="font-medium text-lg">{f.q}</h3>
                    <p className="mt-2 text-muted-foreground leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <ArticleLinkPanel
              links={resolveBnInternalLinks(p.internalLinks)}
              groupOrder={BN_GROUP_ORDER}
              eyebrow="আরো গভীরে যান"
              title="সম্পর্কিত গাইড, সার্ভিস ও রিসোর্স"
              className="mt-16 rounded-3xl border border-border bg-card/50 p-8"
              router={false}
            />
          </article>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">পরামর্শ চান?</div>
              <h3 className="mt-2 font-display text-lg leading-snug">
                আপনার ব্যবসার জন্য ব্যক্তিগত SEO রোডম্যাপ
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                ফ্রি ২০ মিনিটের কলে জানাব আপনার সাইটে প্রথমে কী ঠিক করা দরকার।
              </p>
              <Link
                to="/bn/book"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium"
              >
                যোগাযোগ করুন <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </Section>

      {related.length > 0 && (
        <Section className="pb-24">
          <h2 className="font-display text-2xl mb-6">আরো পড়ুন</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to="/bn/blog/$slug"
                params={{ slug: r.slug }}
                className="group rounded-2xl border border-border bg-card p-5 transition hover:border-primary/40"
              >
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {r.readMin} মিনিট
                </div>
                <h3 className="mt-2 font-display text-base leading-snug group-hover:text-primary transition">
                  {r.title}
                </h3>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {relatedCategories.length > 0 && (
        <Section className="pb-24">
          <div className="rounded-3xl border border-border bg-card/40 p-8 lg:p-12">
            <h2 className="font-display text-2xl md:text-3xl">
              সম্পর্কিত <span className="text-gradient">ক্যাটাগরি</span>
            </h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">
              এই বিষয়ের পাশাপাশি যেসব ট্র্যাকে আরো গভীর গাইড পাবেন।
            </p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {categorySlug && (
                <li>
                  <Link
                    to="/bn/blog/category/$category"
                    params={{ category: categorySlug }}
                    className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs hover:opacity-90 transition"
                  >
                    {p.category}
                    <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </li>
              )}
              {relatedCategories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/bn/blog/category/$category"
                    params={{ category: c.slug }}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
                  >
                    {c.name}
                    <span className="text-[10px] tabular-nums opacity-60">{c.count}</span>
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/bn/blog"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
                >
                  সব আর্টিকেল
                </Link>
              </li>
            </ul>
          </div>
        </Section>
      )}
    </>
  );
}
